import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStores } from '@/stores';
import { config } from '@/lib/config';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { History, Users, CheckCircle, XCircle, Clock, Eye } from 'lucide-react';
import { toast } from 'sonner';

interface NotificationHistoryItem {
  id: string;
  type: string;
  title: string;
  message: string;
  tags: string[];
  target_count: number;
  delivered_count: number;
  failed_count: number;
  flex_message?: string;
  sent_at: string;
  completed_at?: string;
  created: string;
  updated: string;
}

const NotificationHistory: React.FC = observer(() => {
  const { tenantStore } = useStores();
  const [histories, setHistories] = useState<NotificationHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedHistory, setSelectedHistory] = useState<NotificationHistoryItem | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    fetchHistories();
  }, [tenantStore.selectedTenant?.id]);

  const fetchHistories = async () => {
    if (!tenantStore.selectedTenant?.id) return;
    
    setLoading(true);
    try {
      const response = await fetch(
        `${config.apiBaseUrl}/v1/tenants/${tenantStore.selectedTenant.id}/notifications/history`,
        {
          credentials: 'include',
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setHistories(data || []);
      } else {
        toast.error('履歴の取得に失敗しました');
      }
    } catch (error) {
      console.error('Failed to fetch histories:', error);
      toast.error('履歴の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getStatusBadge = (history: NotificationHistoryItem) => {
    const total = history.target_count;
    const delivered = history.delivered_count;
    const failed = history.failed_count;
    const inProgress = total - delivered - failed;
    
    if (inProgress > 0 && !history.completed_at) {
      return (
        <Badge variant="secondary" className="flex items-center gap-1">
          <Clock className="h-3 w-3" />
          配信中
        </Badge>
      );
    }
    
    if (failed === total) {
      return (
        <Badge variant="destructive" className="flex items-center gap-1">
          <XCircle className="h-3 w-3" />
          失敗
        </Badge>
      );
    }
    
    if (failed > 0) {
      return (
        <Badge variant="outline" className="flex items-center gap-1">
          <CheckCircle className="h-3 w-3" />
          一部成功
        </Badge>
      );
    }
    
    return (
      <Badge variant="default" className="flex items-center gap-1">
        <CheckCircle className="h-3 w-3" />
        完了
      </Badge>
    );
  };

  const openDetail = (history: NotificationHistoryItem) => {
    setSelectedHistory(history);
    setDetailOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">読み込み中...</div>
      </div>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            配信履歴
          </CardTitle>
        </CardHeader>
        <CardContent>
          {histories.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              配信履歴がありません
            </div>
          ) : (
            <div className="space-y-4">
              {histories.map((history) => (
                <div
                  key={history.id}
                  className="border rounded-lg p-4 hover:bg-gray-50"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-medium">{history.title}</h3>
                        {getStatusBadge(history)}
                        <Badge variant="outline">
                          {history.type === 'broadcast' ? '全体配信' : 'ターゲット配信'}
                        </Badge>
                      </div>
                      
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                        {history.message}
                      </p>
                      
                      {history.tags && history.tags.length > 0 && (
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm text-gray-500">タグ:</span>
                          <div className="flex gap-1">
                            {history.tags.map((tag) => (
                              <Badge key={tag} variant="secondary" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          <span>対象: {history.target_count}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <CheckCircle className="h-3 w-3 text-green-500" />
                          <span>成功: {history.delivered_count}</span>
                        </div>
                        {history.failed_count > 0 && (
                          <div className="flex items-center gap-1">
                            <XCircle className="h-3 w-3 text-red-500" />
                            <span>失敗: {history.failed_count}</span>
                          </div>
                        )}
                        <div>
                          送信日時: {formatDate(history.sent_at)}
                        </div>
                      </div>
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openDetail(history)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>配信詳細</DialogTitle>
            <DialogDescription>
              配信ID: {selectedHistory?.id}
            </DialogDescription>
          </DialogHeader>
          
          {selectedHistory && (
            <div className="space-y-4">
              <div>
                <h4 className="font-medium mb-1">タイトル</h4>
                <p className="text-gray-700">{selectedHistory.title}</p>
              </div>
              
              <div>
                <h4 className="font-medium mb-1">メッセージ</h4>
                <p className="text-gray-700 whitespace-pre-wrap">
                  {selectedHistory.message}
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium mb-1">配信タイプ</h4>
                  <p className="text-gray-700">
                    {selectedHistory.type === 'broadcast' ? '全体配信' : 'ターゲット配信'}
                  </p>
                </div>
                
                <div>
                  <h4 className="font-medium mb-1">送信日時</h4>
                  <p className="text-gray-700">
                    {formatDate(selectedHistory.sent_at)}
                  </p>
                </div>
              </div>
              
              {selectedHistory.tags && selectedHistory.tags.length > 0 && (
                <div>
                  <h4 className="font-medium mb-1">配信タグ</h4>
                  <div className="flex gap-2">
                    {selectedHistory.tags.map((tag) => (
                      <Badge key={tag} variant="secondary">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              <div>
                <h4 className="font-medium mb-2">配信統計</h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {selectedHistory.target_count}
                      </div>
                      <div className="text-sm text-gray-600">対象</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {selectedHistory.delivered_count}
                      </div>
                      <div className="text-sm text-gray-600">成功</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-red-600">
                        {selectedHistory.failed_count}
                      </div>
                      <div className="text-sm text-gray-600">失敗</div>
                    </div>
                  </div>
                  
                  {selectedHistory.target_count > 0 && (
                    <div className="mt-4">
                      <div className="bg-gray-200 rounded-full h-4 overflow-hidden">
                        <div
                          className="bg-green-500 h-full transition-all"
                          style={{
                            width: `${
                              (selectedHistory.delivered_count /
                                selectedHistory.target_count) *
                              100
                            }%`,
                          }}
                        />
                      </div>
                      <div className="text-center text-sm text-gray-600 mt-1">
                        成功率:{' '}
                        {Math.round(
                          (selectedHistory.delivered_count /
                            selectedHistory.target_count) *
                            100
                        )}
                        %
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {selectedHistory.flex_message && (
                <div>
                  <h4 className="font-medium mb-1">Flex Message</h4>
                  <pre className="bg-gray-100 p-2 rounded text-xs overflow-auto max-h-40">
                    {selectedHistory.flex_message}
                  </pre>
                </div>
              )}
              
              {selectedHistory.completed_at && (
                <div>
                  <h4 className="font-medium mb-1">完了日時</h4>
                  <p className="text-gray-700">
                    {formatDate(selectedHistory.completed_at)}
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
});

export default NotificationHistory;